import Student from '../../../model/Student';
import { Box, IconButton, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CustomTable from '../CustomTable';
import { useContext, useEffect, useRef, useState } from 'react';
import { getThirdBStageStudents, updateThirdBStageResult } from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import Result from '../../../model/Result';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { decodeBase64Utf8 } from '../../../utils/string-tools';
import OlympicsContext from '../../../store/olympics-context';
import { filter } from '../StagesTable';
import { debugError } from '../../../utils/debug';
import ContactLinks from '../ContactLinks';
import ProtocolVerificationDialog from './ProtocolVerificationDialog';

const ThirdBStageTable = () => {
	const [allStudents, setAllStudents] = useState([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
	const { logout, token, isAdmin, isSecretary, id } = useContext(AuthContext);
	const { districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing } = useContext(OlympicsContext);
	const isRefreshingRef = useRef(false);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();

	const stage3TotalPoints = (r: Result) =>
		Number(r.thirdAStagePoints ?? 0) + Number(r.thirdBOneStagePoints ?? 0) + Number(r.thirdBTwoStagePoints ?? 0);

	const filterStudents = (list: Student[]) => {
		let filtered = filter(list, districtFilterId);

		if (searchFilterValue) {
			filtered = filtered.filter((s) => {
				const matchesSearch = (value: string | undefined) =>
					value && value.toLowerCase().includes(searchFilterValue.toLowerCase());

				return (
					matchesSearch(s.firstName) ||
					matchesSearch(s.lastName) ||
					matchesSearch(s.email) ||
					matchesSearch(s.school?.name) ||
					matchesSearch(s.district?.name) ||
					s.teachers?.some((t: any) => matchesSearch(t.firstName) || matchesSearch(t.lastName))
				);
			});
		}

		return filtered;
	};

	useEffect(() => {
		setDisplayedStudents(filterStudents(allStudents));
	}, [districtFilterId, searchFilterValue, allStudents]);

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}
		getThirdBStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
				} else {
					debugError('ThirdBStageTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	const runThirdBPointsSave = (payload: { id: number; thirdBOneStagePoints: number; thirdBTwoStagePoints: number }) => {
		setIsUpdating(true);
		updateThirdBStageResult(token, payload)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
					const warningEncoded = response.headers?.['x-olympics-warning-b64'];
					const warningFallback = response.headers?.['x-olympics-warning'];
					if (warningEncoded || warningFallback) {
						const warning = warningEncoded ? decodeBase64Utf8(warningEncoded) : warningFallback;
						setMsg({ msg: warning, severity: 'warning' });
					}
				} else {
					debugError('ThirdBStageTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	const handleSave = () => {
		if (!editedStudent?.id) {
			return;
		}
		runThirdBPointsSave({
			id: editedStudent.id,
			thirdBOneStagePoints: editedStudent.result.thirdBOneStagePoints,
			thirdBTwoStagePoints: editedStudent.result.thirdBTwoStagePoints,
		});
	};

	/** Ponowne zapisanie tych samych punktów — przelicza tytuł wg aktualnych progów (Olympics). */
	const handleRecalculateTitleFromRow = (student: Student) => {
		if (!isAdmin || isUpdating) {
			return;
		}
		runThirdBPointsSave({
			id: student.id,
			thirdBOneStagePoints: student.result.thirdBOneStagePoints,
			thirdBTwoStagePoints: student.result.thirdBTwoStagePoints,
		});
	};

	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			downloadData(false);
		} else {
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger, token]);

	const handlePointsChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = Number(event.target.value);
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const renderPointsField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				type={'number'}
				value={editedStudent.result[accessor]}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handlePointsChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const handleSelectChange = (event: SelectChangeEvent) => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				result: { ...editedStudent.result, finalTitle: event.target.value },
			});
		}
	};

	const data = (input: any) => {
		const contactColumns = isSecretary ? [{ header: 'Kontakt', accessor: 'contact', align: 'left' }] : [];
		let additionalColumns: any[] = [];

		if (isAdmin || isSecretary) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}
		const specialCertificateColumn =
			isAdmin || isSecretary
				? [{ header: 'Orzeczenie', accessor: 'specialCertificate', align: 'center', width: '120px' }]
				: [];

		return {
			columns: [
				{ header: 'Lp.', accessor: 'index', align: 'left', width: '70px' },
				{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				...additionalColumns,
				...contactColumns,
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left', width: '140px' },
				{ header: 'Nauczyciel', accessor: 'teachers', align: 'left', width: '160px' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				...specialCertificateColumn,
				{ header: 'Specjalizacja', accessor: 'specialization', width: '150px', align: 'center' },
				{ header: 'Temat części ustnej', accessor: 'oralTopic', width: '220px', align: 'left' },
				{ header: 'Temat z językoznawstwa', accessor: 'linguisticTopic', width: '220px', align: 'left' },
				{ header: 'Pytanie I', accessor: 'pointsA', width: '100px', align: 'center', editable: isAdmin },
				{ header: 'Pytanie II', accessor: 'pointsB', width: '100px', align: 'center', editable: isAdmin },
				{ header: 'Suma III', accessor: 'sumIII', width: '100px', align: 'center' },
				{ header: 'Tytuł', accessor: 'title', width: '150px', align: 'center' },
				{ header: '', accessor: 'action', align: 'center', width: '100px', nosort: true, editable: isAdmin },
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							id: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							contact: isSecretary && <ContactLinks student={i} />,
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.cityWithCountry}
								</Typography>
							),
							district: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district?.name || ''}
								</Typography>
							),
							teachers: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{(i.teachers || [])
										.map((t) => `${t.firstName} ${t.lastName}`.trim())
										.filter(Boolean)
										.join(', ')}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClassText && i.schoolClassText.trim() !== '' ? i.schoolClassText : String(i.schoolClass)}
								</Typography>
							),
							specialCertificate: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialCertificate ? 'TAK' : 'NIE'}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							oralTopic: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.topic.topic}
								</Typography>
							),
							linguisticTopic: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.topic.linguisticTopic}
								</Typography>
							),
							pointsA: renderPointsField(i, 'thirdBOneStagePoints'),
							pointsB: renderPointsField(i, 'thirdBTwoStagePoints'),
							sumIII: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{stage3TotalPoints(editedStudent?.id === i.id ? editedStudent.result : i.result)}
								</Typography>
							),
							title: (
								<Tooltip title={isAdmin ? 'Podwójne kliknięcie: przelicz tytuł wg aktualnych progów (jak Zapisz)' : ''}>
									<Box
										component='span'
										onDoubleClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleRecalculateTitleFromRow(i);
										}}
										sx={{
											display: 'inline-block',
											minWidth: '100%',
											cursor: isAdmin ? 'pointer' : 'default',
										}}
									>
										<Typography
											variant='caption'
											fontWeight='medium'
										>
											{i.result.finalTitle}
										</Typography>
									</Box>
								</Tooltip>
							),
							action: (
								<ActionButtons
									student={i}
									disabled={!isAdmin}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSave}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => setEditedStudent(i)}
								/>
							),
						};
					})
				: [],
		};
	};
	const footerButtons = isAdmin ? (
		<Tooltip title='Weryfikacja protokołu (Etap IIIB)'>
			<IconButton
				size='small'
				sx={{ color: 'white' }}
				onClick={() => setVerificationDialogOpen(true)}
			>
				<FileUploadIcon fontSize='small' />
			</IconButton>
		</Tooltip>
	) : null;

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<CustomTable
					data={data(displayedStudents)}
					footerLeft={footerButtons}
				/>
			)}
			<ProtocolVerificationDialog
				open={verificationDialogOpen}
				onClose={() => setVerificationDialogOpen(false)}
				stageKey='IIIB'
				title='Weryfikacja protokołu (Etap IIIB)'
				students={displayedStudents}
			/>
		</>
	);
};

export default ThirdBStageTable;
